import { typographyStyle } from '../../../theme/typographyStyle'
import { makeStyles, Theme } from '@material-ui/core/styles'
/**
 * Styles for SchedulerHeader component
 */
const HEADER_HEIGHT = 160
const HEADER_TITLE_WIDTH = 230
const HEADER_NAV_WIDTH = 480

export const useSchedulerHeaderStyles = makeStyles((theme: Theme) => ({
    mainHeader: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    mainHeaderTopRow: {
        display: 'flex',
        flexDirection: 'row',
        flex: 0,
    },
    mainHeaderBigButtonsRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        paddingLeft: theme.spacing(0),
        paddingRight: theme.spacing(0),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    scheduleHeaderTitle: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: HEADER_TITLE_WIDTH,
        maxWidth: HEADER_TITLE_WIDTH,
        backgroundColor: theme.palette.background.paper,
        marginRight: 0,
    },
    schedulerHeaderNav: {
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        minWidth: 0,
        backgroundColor: theme.palette.background.paper,
        gap: 0,
    },
    schedulerHeaderNavColumns: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 0,
    },
    schedulerHeaderNavLeft: {
        display: 'flex',
        flexDirection: 'column',
        flex: 0,
    },
    analyticsBox: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.75, 1),
        backgroundColor: theme.palette.background.default,
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: theme.spacing(0.5),
    },
    typeOfAnalyticsBox: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    typeOfAnalyticsBoxWrap: {
        marginLeft: theme.spacing(2),
        flexShrink: 0,
    },
    analyticsTypeButtons: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    analyticsTypeButton: {
        minWidth: 0,
        padding: theme.spacing(0.25, 0.75),
        fontSize: '0.75rem',
        textTransform: 'none',
        border: `1px solid ${theme.palette.divider}`,
    },
    analyticsTypeButtonSelected: {
        minWidth: 0,
        padding: theme.spacing(0.25, 0.75),
        fontSize: '0.75rem',
        textTransform: 'none',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: theme.spacing(4),
        overflow: 'hidden',
    },
    titleText: {
        ...(typographyStyle(theme.typography.h6) as Record<string, unknown>),
        fontWeight: 700,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    titleMeta: {
        ...(typographyStyle(theme.typography.subtitle2) as Record<
            string,
            unknown
        >),
        color: theme.palette.text.secondary,
    },
    titleDot: {
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        ...(typographyStyle(theme.typography.subtitle2) as Record<
            string,
            unknown
        >),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    metaItem: {
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
    },
    dateRange: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    scheduleHeaderIcons: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundColor: theme.palette.background.paper,
        marginLeft: 0,
    },
    iconButton: {
        padding: theme.spacing(1),
        width: 38,
        height: 38,
    },
    keyTrackingText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    keyTrackingWrap: {
        position: 'relative',
        width: 38,
        height: 38,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: -5,
    },
    keyTrackingIconButton: {
        width: 38,
        height: 38,
        padding: 0,
        position: 'absolute',
        top: 0,
    },
    keyTrackingBadge: {
        position: 'absolute',
        top: 26,
        padding: '1px 4px',
        fontSize: 10,
        lineHeight: 1.2,
        color: theme.palette.text.secondary,
    },
    buttonRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        minHeight: theme.spacing(4),
        marginTop: theme.spacing(0.5),
    },
    scheduleButton: {
        minWidth: 110,
        maxHeight: 36,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        variant: 'contained',
    },
    buttonText: {
        fontWeight: 700,
        fontSize: 14,
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },
    bigActionButtonsRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: theme.spacing(1.5),
    },
    bigActionButton: {
        minWidth: 100,
        maxHeight: 36,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
        },
    },
    bigActionButtonOutlined: {
        minWidth: 100,
        maxHeight: 36,
        border: `2px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        '&:hover': {
            borderWidth: 2,
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-disabled': {
            borderColor: theme.palette.action.disabled,
            color: theme.palette.action.disabled,
        },
    },
    typeOfAnalyticsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
    },
    typeOfAnalyticsLabel: {
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    statTiles: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
    },
    statTile: {
        padding: theme.spacing(0.75, 1.5),
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        maxWidth: 190,
    },
    statLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    statValue: {
        fontSize: '0.9rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    statContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        whiteSpace: 'nowrap',
    },

    keyStatsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
        justifyContent: 'flex-start',
        marginLeft: theme.spacing(2),
    },
    viewAnalyticsTabs: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
        justifyContent: 'flex-start',
    },
    tabsRoot: {
        minHeight: 28,
    },
    tabRoot: {
        minHeight: 28,
        padding: '2px 8px',
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'none',
    },
}))

export const useSchedulerAnalyticsStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: theme.spacing(1),
    },
    workorderGrid: {
        // backgroundColor: 'rgba(188, 109, 109, 0.4)',
    },
    workorderValueGridItem: {
        textAlign: 'right',
        alignItems: 'center',
        fontWeight: 700,
    },
    analyticsStatTiles: {
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
    },
    analyticsStatTile: {
        padding: theme.spacing(0.5, 0.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(0.5),
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'stretch',
        minWidth: 72,
        maxWidth: 72,
    },
    analyticsStatTileWorkorder: {
        height: 64,
        padding: theme.spacing(0.25, 0.5),
    },
    analyticsStatTileAreaStatus: {
        maxWidth: 120,
        minWidth: 100,
    },
    analyticsStatContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.25),
        width: '100%',
        height: '100%',
    },
    analyticsStatLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
    analyticsStatValue: {
        fontSize: '1.1rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        textAlign: 'right',
        marginTop: 'auto',
    },
    financeTables: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        width: '100%',
    },
    financeTable: {
        minWidth: 220,
        flex: '1 1 220px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(0.5),
        overflow: 'hidden',
    },
    financeTableTitle: {
        ...(typographyStyle(theme.typography.subtitle2) as Record<
            string,
            unknown
        >),
        fontWeight: 700,
        padding: theme.spacing(0.75, 1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    financeTableCell: {
        ...(typographyStyle(theme.typography.body2) as Record<string, unknown>),
        padding: theme.spacing(0.5, 1),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    financeTableValueCell: {
        ...(typographyStyle(theme.typography.body2) as Record<string, unknown>),
        padding: theme.spacing(0.5, 1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        textAlign: 'right',
        fontWeight: 700,
    },
    financeTableRowMuted: {
        color: theme.palette.text.disabled,
    },
    financeTableRowTooltip: {
        cursor: 'help',
    },
}))
